import React, { useEffect, useState } from 'react';
import {
    AlertBanner,
    Button,
    DropdownMenu,
    Input,
    Modal,
    Radio,
    SparkModalPortal,
    TooltipTrigger,
    sparkToast,
} from '@spark';

import { useAuth } from '@/features/auth/AuthContext';
import RecommendedProviderRow from '@/features/settings/components/RecommendedProviderRow.jsx';
import '../settings.css';
import {
    VERTEX_AUTH_MODES,
    VERTEX_BATCH_BUCKET_RECOMMENDATION,
    VERTEX_CROSS_PROJECT_MESSAGE,
    VERTEX_DEFAULT_LOCATION,
    VERTEX_GCP_DEFAULT_MESSAGE,
    VERTEX_LOCATIONS,
    VERTEX_UNSUPPORTED_MESSAGE,
    buildVertexSavePayload,
    canTestVertexConnection,
    effectiveVertexAuthMode,
    hasVertexConfiguration,
    isVertexFormDirty,
    isVertexPanelManageable,
    normalizeVertexGcsBucket,
    validateVertexForm,
    vertexPanelStatus,
} from './vertexFormState.js';

const locationLabel = (value) =>
    VERTEX_LOCATIONS.find((option) => option.value === value)?.label || value;

// Only the two states the form cannot explain by itself: a platform that
// cannot use Vertex at all, and a GCP platform where the provider is
// already live without anything having been saved.
const PANEL_EXPLANATIONS = {
    unsupported: VERTEX_UNSUPPORTED_MESSAGE,
    default_enabled: VERTEX_GCP_DEFAULT_MESSAGE,
};

const VertexCredentialsForm = ({
    credentials,
    onCredentialsUpdate,
    onCredentialsClear,
    onTestConnection,
    recommendedProvider,
    onRecommendedProviderUpdate,
    platformCloudProvider = '',
}) => {
    const { role, isAuthEnabled } = useAuth();
    const isAdmin = !isAuthEnabled || role === 'Admin';

    // Null rather than a fallback mode: with nothing saved and no
    // cloud-derived default, neither radio may render as selected, or the
    // form claims a configuration that was never saved.
    const [authMode, setAuthMode] = useState(
        () => effectiveVertexAuthMode(credentials) || null
    );
    const [project, setProject] = useState(credentials?.vertex_project || '');
    const [location, setLocation] = useState(
        credentials?.vertex_location || VERTEX_DEFAULT_LOCATION
    );
    const [gcsBucket, setGcsBucket] = useState(credentials?.vertex_gcs_bucket || '');
    const [isSaving, setIsSaving] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [confirmingClear, setConfirmingClear] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);

    useEffect(() => {
        setAuthMode(effectiveVertexAuthMode(credentials) || null);
        setProject(credentials?.vertex_project || '');
        setLocation(credentials?.vertex_location || VERTEX_DEFAULT_LOCATION);
        setGcsBucket(credentials?.vertex_gcs_bucket || '');
        setTestResult(null);
    }, [credentials]);

    const formState = { authMode, project, location, gcsBucket };
    const isDirty = isVertexFormDirty(formState, credentials);
    const isConfigured = hasVertexConfiguration(credentials);
    const isManageable = isVertexPanelManageable(credentials, platformCloudProvider);
    const panelStatus = vertexPanelStatus(credentials, platformCloudProvider);
    const canTest = canTestVertexConnection(credentials, platformCloudProvider);

    const handleSave = () => {
        const error = validateVertexForm(formState);
        if (error) {
            sparkToast.error(error);
            return;
        }
        setIsSaving(true);
        setTestResult(null);
        onCredentialsUpdate(buildVertexSavePayload(formState), 'saved');
        setTimeout(() => setIsSaving(false), 500);
    };

    const handleClear = () => {
        setConfirmingClear(false);
        setIsClearing(true);
        setTestResult(null);
        onCredentialsClear();
        setTimeout(() => setIsClearing(false), 500);
    };

    const handleTestConnection = async () => {
        setIsTesting(true);
        setTestResult(null);
        try {
            setTestResult(await onTestConnection());
        } finally {
            setIsTesting(false);
        }
    };

    return (
        <div className="settings-providers__panel">
            <div className="settings-subheading-group">
                <h3 className="settings-subheading fs-unmask">VertexAI Access</h3>
                <p className="settings-text settings-providers__description fs-unmask">
                    SpecificAI calls Vertex AI with its own workload identity, so there
                    is no key to upload. Grant that service account access to Vertex AI
                    in your project. Need help? See the
                    {' '}
                    <a
                        className="settings-link"
                        href="https://cloud.google.com/kubernetes-engine/docs/how-to/workload-identity"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        Workload Identity guide
                    </a>
                    .
                </p>
            </div>

            {PANEL_EXPLANATIONS[panelStatus] ? (
                <AlertBanner
                    feedback="info"
                    text={PANEL_EXPLANATIONS[panelStatus]}
                    showButton={false}
                    showCloseButton={false}
                />
            ) : null}

            <div className="settings-form">
                <div className="settings-field-group fs-unmask">
                    <span className="settings-field-group__label">Vertex AI project</span>
                    <Radio
                        name="vertex-auth-mode"
                        label="Same GCP project as SpecificAI"
                        value={VERTEX_AUTH_MODES.SAME_PROJECT}
                        status={
                            authMode === VERTEX_AUTH_MODES.SAME_PROJECT
                                ? 'active'
                                : 'inactive'
                        }
                        disabled={!isAdmin || !isManageable}
                        onChange={() => setAuthMode(VERTEX_AUTH_MODES.SAME_PROJECT)}
                    />
                    <TooltipTrigger text={VERTEX_CROSS_PROJECT_MESSAGE}>
                        <span>
                            <Radio
                                name="vertex-auth-mode"
                                label="A different GCP project (coming soon)"
                                value={VERTEX_AUTH_MODES.CROSS_PROJECT}
                                status={
                                    authMode === VERTEX_AUTH_MODES.CROSS_PROJECT
                                        ? 'active'
                                        : 'inactive'
                                }
                                disabled
                            />
                        </span>
                    </TooltipTrigger>
                </div>

                <Input
                    size="large"
                    label="Vertex Project"
                    value={project}
                    onChange={(e) => setProject(e.target.value)}
                    placeholder="Auto-detected"
                    disabled={!isAdmin || !isManageable}
                    className="settings-secret__input"
                />
                <span className="settings-text">
                    Leave empty to use the project SpecificAI runs in.
                </span>

                <div className="settings-field-group fs-unmask">
                    <span className="settings-field-group__label">Vertex Location</span>
                    <DropdownMenu
                        size="medium"
                        showLabel={false}
                        items={VERTEX_LOCATIONS.map((option) => ({ label: option.label }))}
                        value={locationLabel(location)}
                        text={locationLabel(location)}
                        disabled={!isAdmin || !isManageable}
                        onSelect={(item) => {
                            const selected = VERTEX_LOCATIONS.find(
                                (option) => option.label === item.label
                            );
                            if (selected) setLocation(selected.value);
                        }}
                    />
                </div>

                <Input
                    size="large"
                    label="Batch Jobs Bucket (recommended)"
                    value={gcsBucket}
                    onChange={(e) => setGcsBucket(e.target.value)}
                    placeholder="my-vertex-batch-bucket"
                    disabled={!isAdmin || !isManageable}
                    className="settings-secret__input"
                />
                <span className="settings-text">
                    Cloud Storage bucket for batch prediction job input/output.
                    Optional — leave empty to disable Vertex batch. The platform
                    service account needs read/write access to this bucket.
                </span>
                {isManageable && !normalizeVertexGcsBucket(gcsBucket) ? (
                    <AlertBanner
                        feedback="info"
                        text={VERTEX_BATCH_BUCKET_RECOMMENDATION}
                        showButton={false}
                        showCloseButton={false}
                    />
                ) : null}
            </div>

            {testResult ? (
                <AlertBanner
                    feedback={testResult.success ? 'success' : 'error'}
                    text={
                        testResult.success
                            ? `Connected. ${testResult.model_count} model(s) available.`
                            : testResult.error
                    }
                    showButton={false}
                    onCloseClick={() => setTestResult(null)}
                />
            ) : null}

            <div className="settings-form__footer settings-form__footer--gap">
                <Button
                    size="tiny"
                    variant="secondary"
                    danger
                    disabled={!isAdmin || !isManageable || !isConfigured || isClearing}
                    disableWhenReadOnly
                    onClick={() => setConfirmingClear(true)}
                >
                    Clear
                </Button>
                <Button
                    size="tiny"
                    variant="secondary"
                    disabled={!isAdmin || !canTest || isDirty || isTesting}
                    onClick={handleTestConnection}
                >
                    Test connection
                </Button>
                <Button
                    size="tiny"
                    variant="primary"
                    disabled={!isAdmin || !isManageable || !isDirty || isSaving}
                    disableWhenReadOnly
                    onClick={handleSave}
                >
                    Save Changes
                </Button>
            </div>

            {confirmingClear ? (
                <SparkModalPortal
                    open
                    onClose={() => setConfirmingClear(false)}
                    size="text"
                >
                    <Modal
                        title="Clear Vertex configuration?"
                        subtitle="This removes the saved Vertex AI configuration from your organization."
                        showScrollBar={false}
                        fixedHeight={false}
                        showOverlay={false}
                        danger
                        primaryLabel="Clear"
                        tertiaryLabel="Cancel"
                        primaryDisableWhenReadOnly
                        onPrimaryClick={handleClear}
                        onTertiaryClick={() => setConfirmingClear(false)}
                        onCloseClick={() => setConfirmingClear(false)}
                    />
                </SparkModalPortal>
            ) : null}

            <RecommendedProviderRow
                provider="vertex"
                recommendedProvider={recommendedProvider}
                onRecommendedProviderUpdate={onRecommendedProviderUpdate}
            />
        </div>
    );
};

export default VertexCredentialsForm;
